import { useState, useEffect, useContext } from 'react'
import CommentApi from '../../../api/CommentApi';
import DebounceBtn from '../../common/DebounceBtn';
import TimestampDisplay from '../../common/TimestampDisplay';
import { isUsingAnnanymous } from '../../../constant/PostClass';
import UserContext from '../../../context/UserContext';
import { resolveCommentProfileImg, resolveUserProfileImg } from '../../../util/ProfileUtil';

const WRITER_SEPERATOR = '/++writer-seperator++/';

const STYLE = {
  COMMENT_PARENT_WRITER : {
    fontWeight: 'bold',
    color: '#5d8fcd94',
    marginRight: '5px'
  }
}

const CommentWithWriter = ({ comment }) => {
  const commentInfo = comment.split(WRITER_SEPERATOR);
  
  if (commentInfo.length < 2) return (
    <div class="comment-text">
      <pre>{comment}</pre>
    </div>
  );
  
  const [writer, content] = commentInfo;
  return (
    <div class="comment-text">
      <pre style={{wordBreak: 'break-word'}}>
        {writer && <span className="comment-writer" style={STYLE.COMMENT_PARENT_WRITER}>@{writer}</span>}
        {content}
      </pre>
    </div>
  )
    
}

const CommentItem = ({ postType, comment, isSecond=false, onAdded }) => {
  const {
    comment_user_id, 
    comment_id, comment_writer_nicknm, comment_content, comment_reg_date, comment_status,
    comment_post_id, comment_parent_id, children=[], comment_like_cnt
  } = comment;
  
  const [ isReplyOpen, setIsReplyOpen ] = useState(false);
  const [ isDeleted, setIsDeleted ] = useState(comment_status === 'deleted');
  const [ curCommentLikeCnt, setCurCommentLikeCnt ] = useState(comment_like_cnt || 0);

  const handleDeleteBtnClick = async () => {
    const rslt = await CommentApi.deleteComment(comment_id);
    if (rslt.code === 'fail') alert('댓글 삭제에 실패하였습니다. 잠시후 다시 시도해주세요.');
    setIsDeleted(true);
  }

  const handleReplyBtnClick = () => {
    setIsReplyOpen(!isReplyOpen);
  }

  const handleLikeBtnClick = async () => {
    const rslt = await CommentApi.likeComment(comment_id);  
    if (rslt.code === 'fail') {
      if (rslt.message) return alert(rslt.message);
      return alert('댓글 좋아요에 실패했습니다. 잠시후 다시 시도해주세요');
    }
    setCurCommentLikeCnt(curCommentLikeCnt + 1);
  }

  const handleOnAdded = newComment => {
    setIsReplyOpen(false);
    onAdded(newComment);
  }

  if (isDeleted) {
    return (
      <>
        <div className={`comment-node ${isSecond ? 'comment-node-depth' : ''}`}>
          <div class="comment-text">
            <pre>삭제된 댓글입니다.</pre>
          </div>
        </div>
        {children.map(c => <CommentItem key={c.comment_id} postType={postType} comment={c} isSecond={true} onAdded={handleOnAdded} />)}
      </>
    )
  }
  
  return (
    <>
      <div className={`comment-node ${isSecond ? 'comment-node-depth' : ''}`}>
        <div class="comment-header">
          <div class="comment-profile">
            <div class="comment-profile-thumb">
              <img src={resolveCommentProfileImg(comment, postType)} />
            </div>
            <div class="comment-profile-name">{comment_writer_nicknm}</div>
          </div>
          {comment_user_id &&
            <button type="button" onClick={handleDeleteBtnClick}>
              <span class="text-indent">삭제</span>
                <i class="icon icon-trash"></i>
            </button>
          }
        </div>
          
        <CommentWithWriter comment={comment_content} />
        
        <div class="comment-tool">
          <span><TimestampDisplay timestamp={comment_reg_date} /></span>
          <button type="button" onClick={handleLikeBtnClick}>
            <i class="icon icon-like"></i>
            <span>좋아요 {curCommentLikeCnt}</span>
          </button>
          <button type="button" onClick={handleReplyBtnClick}>
            <span>댓글쓰기</span>
          </button>
        </div>
        
        {isReplyOpen && 
          <CommentTextarea 
            postId={comment_post_id} 
            postType={postType}
            parentId={comment_parent_id || comment_id} 
            onAdded={handleOnAdded} 
            isSecond={true}
            upperWriter={comment_writer_nicknm} 
          />
        }
      </div>
      
      {children.map(c => <CommentItem postType={postType} comment={c} isSecond={true} onAdded={handleOnAdded} />)}
    </>
  )
}

const buildCommentContent = (content, writer) => {
  return `${writer}${WRITER_SEPERATOR}${content}`
}

const CommentTextarea = ({ postId, postType, parentId, onAdded, isSecond = false, upperWriter = '' }) => {
  const { user } = useContext(UserContext);
  const [ comment, setComment ] = useState('');
  let ingFlag = false;

  const handleCommentRegisterClick = async () => {
    if (ingFlag) return;
    if (comment.trim().length === 0) return alert('내용이 없습니다.');
    ingFlag = true;
    const nicknm = isUsingAnnanymous(postType) ? undefined : user.userNicknm;
    const rslt = await CommentApi.createComment(postId, parentId, buildCommentContent(comment, upperWriter), nicknm);
    if (rslt.code === 'fail') return alert(rslt.message || '댓글 작성에 실패했습니다. 잠시후 다시 시도해주세요');
    rslt.comment.writer_profile_img = resolveUserProfileImg(user);
    onAdded(rslt.comment);
    setComment('');
  }
  
  const handleTextareaKeyUp = e => {
    if (e.which !== 13) return;
    handleCommentRegisterClick();
  }

  return (
    <div className={`comment-form ${isSecond ? 'mb-15 pl-16' : ''}`}>
      <textarea
        onChange={e => setComment(e.target.value)}
        value={comment}
        placeholder="댓글을 입력하세요"
      ></textarea>
      <DebounceBtn className='btn btn-primary' onClick={handleCommentRegisterClick}>등록</DebounceBtn>
    </div>
  )
}

const Comment = ({ postId, postType }) => {
    const [ comments, setComments ] = useState([]);
    const [ totalCnt, setTotalCnt ] = useState(0);

    const handleOnAdded = newComment => {
      const newComments = [...comments]
      if (!newComment.comment_parent_id) {
        newComments.push(newComment);
      } else {
        for(let c of newComments) {
          if (c.comment_id === newComment.comment_parent_id) {
            if (!c.children) c.children = [];
            c.children.push(newComment);
            break;
          }
        }
      }
      setComments(newComments);
      setTotalCnt(totalCnt + 1);
    }

    useEffect(() => {
      CommentApi.getComments(postId).then(rslt => {
        setComments(rslt.comments);
        setTotalCnt(rslt.totalCount);
      })
    }, [postId])

    return (
      <div className='section-area'>
        
        <div className='section-header'>
          <h1 className='section-header-title'>댓글 {totalCnt}</h1>
        </div>
        
        <div className="section-content">
          <div className='comment-histoy'>
              {comments.map(c => <CommentItem key={c.comment_id} postType={postType} comment={c} onAdded={handleOnAdded}/>)}
          </div>
          
        </div>
        <CommentTextarea postId={postId} postType={postType} onAdded={handleOnAdded} />
      </div>
    )
}

export default Comment